// What reaches Prime's session that is not the owner's words (the factory's
// news, a fired watch, a message from a member's Prime): the framing says so to
// the model ("information, not the owner's words") and is never shown to the
// owner. Each is drawn as its own kind of line in the thread; every surface that
// shows the lead's lines reads them through here. The prefixes are Go's
// (assistant.NewsPrefix and factoryprov.BuildPrefix, assistant.WatchPrefix,
// assistant.MemberPrimePrefix); a curly apostrophe or a missing newline still
// counts, since the factory may hand a line back retyped.

/** How the factory's news to Prime begins (assistant.NewsPrefix, factoryprov.BuildPrefix in Go). */
export const NEWS_PREFIX = "News from the software factory (information, not the owner's words):\n"
/** How a fired watch reaches Prime (assistant.WatchPrefix in Go). */
export const WATCH_PREFIX = "A watch you set has fired (information, not the owner's words):\n"

const NEWS_RE = /^\s*News from the software factory \(information, not the owner['’]s words\):\s*/
const WATCH_RE = /^\s*A watch you set has fired \(information, not the owner['’]s words\):\s*/
const MEMBER_RE = /^\s*A message from (.+?)['’]s Prime, on her side \([^)]*\):\s*/

export type AppLine =
  | { kind: 'news'; body: string }
  | { kind: 'watch'; body: string }
  | { kind: 'member'; who: string; body: string }

/** A line the app or the factory wrote for Prime, with its framing taken off; undefined for anything else. */
export const appLineOf = (text: string): AppLine | undefined => {
  const news = NEWS_RE.exec(text)
  if (news) return { kind: 'news', body: text.slice(news[0].length) }
  const watch = WATCH_RE.exec(text)
  if (watch) return { kind: 'watch', body: text.slice(watch[0].length) }
  const member = MEMBER_RE.exec(text)
  if (member) return { kind: 'member', who: member[1] ?? '', body: text.slice(member[0].length) }
  return undefined
}

/**
 * The news in a line: the agent it is about (when it names one, by Go's
 * `Coding agent "<title>" (id <uuid>, …)`) and its first line.
 */
export const newsParts = (body: string): { title?: string; id?: string; first: string } => {
  const id = /\(id ([0-9a-f-]{36}),/.exec(body)?.[1]
  const title = /^Coding agent "(.*)" \(id /m.exec(body)?.[1] ?? /^Agent "([^"]+)"/.exec(body)?.[1]
  const first = (body.split('\n').find((l) => l.trim()) ?? '').trim()
  return { title, id, first }
}
